import type { SitePackageId, CanonicalPresentationModelV1 } from "@enrein/shared-contracts";
import type {
  StudioDepartureKitStatusV1,
  StudioIntakeSummaryV1,
  StudioPackageResolutionV1,
} from "@enrein/shared-contracts";
import type { StudioApiClient } from "../../../api.js";
import type { PackageEligibilityBlocked } from "../../studio-shared/workflow/studio-workflow.js";
import { sitePackageDisplayLabel } from "../../studio-shared/site-package-labels.js";
import {
  isPreviewHtmlDisplaySafe,
  isPreviewOpenAllowed,
  type PreviewOpenGuardInput,
} from "../../studio-shared/preview-open-guard.js";
import {
  CLIENT_SHARE_QA_WARN_ACK_LABEL,
  CLIENT_SHARE_QA_WARN_MESSAGE,
  CLIENT_SHARE_QA_WARN_PRIMARY_CTA,
  isClientReadyShareAllowed,
  requiresWarnShareAcknowledgement,
  shouldOverrideHeroForQaWarn,
  type ClientShareGuardInput,
} from "../../studio-shared/share-guard.js";
import {
  humanDepartureKitIssuanceLabel,
  type SimpleProjectPhaseState,
} from "../../studio-shared/orchestration/project-phase.js";
import type { V10ControlRoomSection } from "../adapters/v10-phase-model.js";
import { V10_MODE_LABELS, V10_SECTION_LABELS } from "../adapters/v10-phase-model.js";
import { isV10ProjectMode } from "../adapters/v10-phase-model.js";
import {
  resolveV10PanelBlocks,
  type V10PanelBlocks,
  type V10SectionProgressStatus,
} from "../adapters/v10-project-adapters.js";
import { v10ProjectRoute, v10ViewRoute } from "../adapters/v10-button-endpoints.js";
import { V10PackageEligibilityCard } from "../components/V10PackageEligibilityCard.js";
import { V10PackageDowngradeCard } from "../components/V10PackageDowngradeCard.js";
import { V10PhaseStrip } from "../components/V10PhaseStrip.js";
import { V10Btn, V10Kicker } from "../components/V10Primitives.js";
import { V10ImportConfirmationPanel } from "./V10ImportConfirmationPanel.js";
import { Block1GovernanceJourney } from "../../../components/Block1GovernanceJourney.js";
import { Block1WorkAssignment } from "../../../components/Block1WorkAssignment.js";
import { V10RichnessPanel } from "./V10RichnessPanel.js";
import { canExposePilotSurface } from "@enrein/shared-contracts/pilot-exposure";
import { V10ProjectRoomEvidence } from "../components/V10ProjectRoomEvidence.js";
import { V10DomainAtlasNav } from "../components/V10DomainAtlasNav.js";
import { V10AgencyExportSuite } from "../components/V10AgencyExportSuite.js";
import { V10RecordCenterPanel } from "../components/V10RecordCenterPanel.js";
import { V10PreviewRouteTabs } from "../components/V10PreviewRouteTabs.js";
import {
  V10ResolveIssuesPanel,
  type StudioQaIssue,
} from "../components/V10ResolveIssuesPanel.js";
import type { V10Navigate, V10RouteState } from "../routing/v10-routing.js";

type PhaseStateWithFacts = SimpleProjectPhaseState & {
  readonly facts: SimpleProjectPhaseState["facts"];
  /** Canonical Agency model that produced the hero, when a presentation adapter owns the moment. */
  readonly heroPresentation?: CanonicalPresentationModelV1 | null;
};

function SetupBlock({
  intakeStatusLabel,
  modeLabel,
  onCopyClientLink,
  onOpenPortal,
}: {
  readonly intakeStatusLabel: string;
  readonly modeLabel: string;
  readonly onCopyClientLink: () => void;
  readonly onOpenPortal: () => void;
}) {
  return (
    <section className="v10-panel-block" aria-label="Setup">
      <p>Intake status: {intakeStatusLabel}</p>
      <p className="v10-muted">
        Share the client link so your client can submit business details. Build unlocks after intake is submitted.
      </p>
      <p>Project mode: {modeLabel}</p>
      <div className="v10-hero-actions">
        <V10Btn onClick={onCopyClientLink}>Copy Client Link</V10Btn>
        <V10Btn variant="gold" onClick={onOpenPortal}>
          Send Client Link
        </V10Btn>
      </div>
    </section>
  );
}

function FactsBlock({
  phaseState,
  intakeSummary,
  validationIssues,
  qaIssues,
  qaWarnings,
  requestedSitePackageId,
  builtSitePackageId,
  previewRoutes,
  packageEligibilityBlocked,
  packageResolution,
  workflowBusy,
  importQueueAvailable,
  onBuildWithPackage,
  onEnrichProfile,
  onReviewTruthProof,
  onCopyClientIntakeLink,
  onReviewImportConfirmation,
  onRetryBuild,
  api,
  projectId,
  onImportQueueChanged,
  showRecordCenter,
}: {
  readonly phaseState: PhaseStateWithFacts | null;
  readonly intakeSummary: StudioIntakeSummaryV1 | null;
  readonly validationIssues: readonly string[];
  readonly qaIssues: readonly StudioQaIssue[];
  readonly qaWarnings: readonly string[];
  readonly requestedSitePackageId: string;
  readonly builtSitePackageId: string | null;
  readonly previewRoutes: readonly string[];
  readonly packageEligibilityBlocked: PackageEligibilityBlocked | null;
  readonly packageResolution: StudioPackageResolutionV1 | null;
  readonly workflowBusy: boolean;
  readonly importQueueAvailable: boolean;
  readonly onBuildWithPackage: (packageId: SitePackageId) => void;
  readonly onEnrichProfile: () => void;
  readonly onReviewTruthProof: () => void;
  readonly onCopyClientIntakeLink: () => void;
  readonly onReviewImportConfirmation: () => void;
  readonly onRetryBuild: () => void;
  readonly api: StudioApiClient;
  readonly projectId: string;
  readonly onImportQueueChanged?: () => void;
  readonly showRecordCenter: boolean;
}) {
  const facts = phaseState?.facts;
  const packageMismatch =
    !packageResolution?.downgradeApplied &&
    builtSitePackageId &&
    requestedSitePackageId &&
    builtSitePackageId !== requestedSitePackageId;
  return (
    <section className="v10-panel-block" aria-label="Truth and proof">
      {packageEligibilityBlocked ? (
        <V10PackageEligibilityCard
          blocked={packageEligibilityBlocked}
          busy={workflowBusy}
          onBuildFallback={onBuildWithPackage}
          onEnrichProfile={onEnrichProfile}
        />
      ) : null}
      {packageResolution?.downgradeApplied ? (
        <V10PackageDowngradeCard
          resolution={packageResolution}
          busy={workflowBusy}
          onContinueWithBuilt={onReviewTruthProof}
          onEnrichProfile={onEnrichProfile}
          onCopyClientIntakeLink={onCopyClientIntakeLink}
          onRetryBuild={onRetryBuild}
        />
      ) : null}
      <V10ResolveIssuesPanel
        qaIssues={qaIssues}
        qaReasons={qaWarnings}
        importQueueAvailable={importQueueAvailable}
        workflowBusy={workflowBusy}
        onReviewTruthProof={onReviewTruthProof}
        onCopyClientIntakeLink={onCopyClientIntakeLink}
        onReviewImportConfirmation={onReviewImportConfirmation}
        onRetryBuild={onRetryBuild}
      />
      {phaseState?.qaFailure ? (
        <>
          <p className="v10-validation">Quality gate failed.</p>
          {packageMismatch ? (
            <p className="v10-muted">
              Requested: {sitePackageDisplayLabel(requestedSitePackageId)} · Built:{" "}
              {sitePackageDisplayLabel(builtSitePackageId)} ({previewRoutes.length} pages)
            </p>
          ) : null}
        </>
      ) : null}
      {facts ? (
        <>
          <p>
            <strong>{facts.businessName}</strong> — {facts.contact}
          </p>
          <p>Services: {facts.services.length > 0 ? facts.services.join(", ") : "—"}</p>
          <p>Service areas: {facts.serviceAreas.length > 0 ? facts.serviceAreas.join(", ") : "—"}</p>
          <p>{facts.description}</p>
        </>
      ) : intakeSummary ? (
        <>
          <p className="v10-cl-kicker">Submitted by client</p>
          <p>
            <strong>{intakeSummary.businessName}</strong>
            {" — "}
            {[intakeSummary.primaryPhone, intakeSummary.primaryEmail].filter(Boolean).join(" · ") || "—"}
          </p>
          <p>
            Services:{" "}
            {intakeSummary.services && intakeSummary.services.length > 0
              ? intakeSummary.services.map((s) => s.name).join(", ")
              : "—"}
          </p>
          <p>
            Service areas:{" "}
            {intakeSummary.serviceAreas && intakeSummary.serviceAreas.length > 0
              ? intakeSummary.serviceAreas.map((a) => a.name).join(", ")
              : "—"}
          </p>
          {intakeSummary.description ? <p>{intakeSummary.description}</p> : null}
        </>
      ) : (
        <p className="v10-muted">Confirm facts, then build preview from the hero action.</p>
      )}
      {validationIssues.length > 0 ? (
        <ul className="v10-validation-list">
          {validationIssues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      ) : null}
      {showRecordCenter ? (
        <V10RecordCenterPanel api={api} projectId={projectId} />
      ) : (
        <>
          <div id="v10-import-confirmation">
            <V10ImportConfirmationPanel api={api} onQueueChanged={onImportQueueChanged} />
          </div>
          <div id="v10-block1-governance">
            <Block1GovernanceJourney api={api} projectId={projectId} canMutate={true} />
            <Block1WorkAssignment api={api} projectId={projectId} canMutate={true} />
          </div>
          {canExposePilotSurface("studio.richnessPanel", "agency", { deployEnv: import.meta.env.VITE_ENREIN_DEPLOY_ENV, nodeEnv: import.meta.env.MODE, explicitFlags: import.meta.env.VITE_ENREIN_PILOT_FEATURE_FLAGS }).enabled ? <V10RichnessPanel api={api} projectId={projectId} /> : null}
          <p>Readiness snapshot: review before client share.</p>
        </>
      )}
    </section>
  );
}

function PreviewBlock({
  projectName,
  studioPreviewHtml,
  previewError,
  previewRoutes,
  activePreviewRoute,
  onPreviewRouteChange,
  previewUrl,
  showPreviewPanel,
  qaWarnings,
  previewOpenGuardInput,
  clientShareGuardInput,
  onWarnShareAcknowledgedChange,
  onShareClientLinkFromPreview,
  onOpenPreview,
  onCopyPreviewLink,
  onResolveQualityIssues,
}: {
  readonly projectName: string;
  readonly studioPreviewHtml: string | null;
  readonly previewError: string | null;
  readonly previewRoutes: readonly string[];
  readonly activePreviewRoute: string;
  readonly onPreviewRouteChange: (route: string) => void;
  readonly previewUrl: string | null;
  readonly showPreviewPanel: boolean;
  readonly qaWarnings: readonly string[];
  readonly previewOpenGuardInput: PreviewOpenGuardInput;
  readonly clientShareGuardInput: ClientShareGuardInput;
  readonly onWarnShareAcknowledgedChange: (acknowledged: boolean) => void;
  readonly onShareClientLinkFromPreview: () => void;
  readonly onOpenPreview: () => void;
  readonly onCopyPreviewLink: () => void;
  readonly onResolveQualityIssues: () => void;
}) {
  const inlinePreviewSafe = isPreviewHtmlDisplaySafe(studioPreviewHtml);
  const previewOpenAllowed = isPreviewOpenAllowed(previewOpenGuardInput);
  const showPreview = Boolean(inlinePreviewSafe || previewUrl || showPreviewPanel);
  const previewReady = previewOpenAllowed;
  const warnShare = requiresWarnShareAcknowledgement(clientShareGuardInput);
  const clientShareAllowed = isClientReadyShareAllowed(clientShareGuardInput);

  return (
    <section className="v10-panel-block" aria-label="Website preview">
      {warnShare ? (
        <p className="v10-validation" role="status">
          Preview generated with warnings. Not client-ready until issues are resolved.
        </p>
      ) : null}
      {qaWarnings.length > 0 ? (
        <ul className="v10-validation-list" aria-label="Preview quality warnings">
          {qaWarnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}
      <V10PreviewRouteTabs
        routes={previewRoutes}
        activeRoute={activePreviewRoute}
        onRouteChange={onPreviewRouteChange}
        disabled={!inlinePreviewSafe}
      />
      {inlinePreviewSafe && studioPreviewHtml ? (
        <div className="v10-preview-box" aria-label="Website preview">
          <div className="v10-preview-html preview-frame" dangerouslySetInnerHTML={{ __html: studioPreviewHtml }} />
        </div>
      ) : previewError ? (
        <p className="v10-validation">{previewError}</p>
      ) : showPreview ? (
        <p className="v10-muted">Loading preview HTML…</p>
      ) : (
        <div className="v10-preview-box" aria-label="Website preview">
          <div className="v10-preview-browser">
            <span />
            <span />
            <span />
          </div>
          <div className="v10-fake-site">
            <h4>{projectName}</h4>
            <p className="v10-muted">Preview will appear when generation completes.</p>
          </div>
        </div>
      )}
      {warnShare ? (
        <label className="v10-muted" style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
          <input
            type="checkbox"
            checked={Boolean(clientShareGuardInput.warnShareAcknowledged)}
            onChange={(event) => onWarnShareAcknowledgedChange(event.target.checked)}
          />
          <span>{CLIENT_SHARE_QA_WARN_ACK_LABEL}</span>
        </label>
      ) : null}
      <div className="v10-hero-actions">
        {warnShare ? (
          <V10Btn variant="gold" onClick={onResolveQualityIssues}>
            {CLIENT_SHARE_QA_WARN_PRIMARY_CTA}
          </V10Btn>
        ) : (
          <V10Btn variant="gold" onClick={onShareClientLinkFromPreview} disabled={!clientShareAllowed}>
            Share Client Link
          </V10Btn>
        )}
        <V10Btn onClick={onOpenPreview} disabled={!previewReady}>
          Open preview
        </V10Btn>
        <V10Btn
          variant={warnShare ? "line" : undefined}
          onClick={onCopyPreviewLink}
          disabled={!previewReady || !clientShareAllowed}
        >
          {warnShare ? "Copy preview link (not client-ready)" : "Copy preview link"}
        </V10Btn>
        {warnShare ? (
          <V10Btn
            variant="line"
            onClick={onShareClientLinkFromPreview}
            disabled={!clientShareAllowed}
          >
            Share Client Link (acknowledged)
          </V10Btn>
        ) : null}
      </div>
      {warnShare && !clientShareAllowed ? (
        <p className="v10-muted">{CLIENT_SHARE_QA_WARN_MESSAGE}</p>
      ) : null}
    </section>
  );
}

function L2EditsBlock({
  changeRequestPreview,
  onOpenPortal,
}: {
  readonly changeRequestPreview: string | null;
  readonly onOpenPortal: () => void;
}) {
  return (
    <section className="v10-panel-block" aria-label="L2 edits">
      {changeRequestPreview ? (
        <>
          <p className="v10-cl-kicker">Client change request</p>
          <blockquote className="v10-client-message">{changeRequestPreview}</blockquote>
        </>
      ) : (
        <p>Review client change requests in the client portal.</p>
      )}
      <div className="v10-hero-actions">
        <V10Btn variant="primary" onClick={onOpenPortal}>
          Open client portal
        </V10Btn>
      </div>
    </section>
  );
}

function ApprovalBlock({
  phaseState,
  previewReady,
  clientShareGuardInput,
  onOpenPreview,
  onCopyPreviewLink,
  onResolveQualityIssues,
}: {
  readonly phaseState: PhaseStateWithFacts | null;
  readonly previewReady: boolean;
  readonly clientShareGuardInput: ClientShareGuardInput;
  readonly onOpenPreview: () => void;
  readonly onCopyPreviewLink: () => void;
  readonly onResolveQualityIssues: () => void;
}) {
  const warnShare = requiresWarnShareAcknowledgement(clientShareGuardInput);
  const clientShareAllowed = isClientReadyShareAllowed(clientShareGuardInput);
  return (
    <section className="v10-panel-block" aria-label="Client approval">
      <p>{phaseState?.nowCopy ?? "Waiting for client decision."}</p>
      {warnShare ? (
        <p className="v10-validation" role="status">
          Preview generated with warnings. Not client-ready until issues are resolved.
        </p>
      ) : null}
      {phaseState?.showStaleApprovalBanner ? (
        <p className="v10-validation">{phaseState.staleApprovalSummary}</p>
      ) : null}
      {previewReady ? (
        <div className="v10-hero-actions">
          <V10Btn onClick={onOpenPreview}>Open preview</V10Btn>
          {warnShare ? (
            <V10Btn variant="gold" onClick={onResolveQualityIssues}>
              {CLIENT_SHARE_QA_WARN_PRIMARY_CTA}
            </V10Btn>
          ) : null}
          <V10Btn onClick={onCopyPreviewLink} disabled={!clientShareAllowed}>
            {warnShare ? "Copy preview link (not client-ready)" : "Copy preview link"}
          </V10Btn>
        </div>
      ) : null}
    </section>
  );
}

function HandoffBlock({
  phaseState,
  departureKit,
  sitePackageId,
  requestedSitePackageId,
  builtSitePackageId,
  previewRoutes,
  exportRunId,
  handoffError,
  previewReady,
  onOpenPreview,
  onDownloadDepartureKit,
  onRequestRunExport,
  onOpenApprovalSection,
  onScrollToEvidence,
  api,
  projectId,
  isExternalProof,
}: {
  readonly phaseState: PhaseStateWithFacts | null;
  readonly departureKit: StudioDepartureKitStatusV1 | null;
  readonly sitePackageId: string | null;
  readonly requestedSitePackageId: string;
  readonly builtSitePackageId: string | null;
  readonly previewRoutes: readonly string[];
  readonly exportRunId: string | null;
  readonly handoffError: string | null;
  readonly previewReady: boolean;
  readonly onOpenPreview: () => void;
  readonly onDownloadDepartureKit: () => void;
  readonly onRequestRunExport: () => void;
  readonly onOpenApprovalSection: () => void;
  readonly onScrollToEvidence: () => void;
  readonly api: StudioApiClient;
  readonly projectId: string;
  readonly isExternalProof: boolean;
}) {
  const packageMismatch =
    builtSitePackageId &&
    requestedSitePackageId &&
    builtSitePackageId !== requestedSitePackageId;
  return (
    <section className="v10-panel-block" aria-label="Handoff">
      <p>{phaseState?.nowCopy ?? "Package departure kit when client approves."}</p>
      {departureKit ? (
        <dl className="v10-handoff-meta">
          <div>
            <dt>Status</dt>
            <dd>{humanDepartureKitIssuanceLabel(departureKit.issuanceStatus)}</dd>
          </div>
          {sitePackageId ? (
            <div>
              <dt>Site package</dt>
              <dd>{sitePackageDisplayLabel(sitePackageId)}</dd>
            </div>
          ) : null}
          {packageMismatch ? (
            <div>
              <dt>Package note</dt>
              <dd>
                Requested: {sitePackageDisplayLabel(requestedSitePackageId)} · Built:{" "}
                {sitePackageDisplayLabel(builtSitePackageId)} ({previewRoutes.length} pages)
              </dd>
            </div>
          ) : null}
          {previewRoutes.length > 0 ? (
            <div>
              <dt>Pages</dt>
              <dd>{previewRoutes.length}</dd>
            </div>
          ) : null}
          {exportRunId ? (
            <div>
              <dt>Run</dt>
              <dd className="v10-mono">{exportRunId}</dd>
            </div>
          ) : null}
          {departureKit.artifactSetHash ? (
            <div>
              <dt>Version fingerprint</dt>
              <dd className="v10-mono">{departureKit.artifactSetHash.slice(0, 16)}…</dd>
            </div>
          ) : null}
        </dl>
      ) : (
        <p className="v10-muted">Handoff status loads when export starts.</p>
      )}
      <p>Departure Kit download: {departureKit?.downloadAvailable ? "Ready" : "Not ready yet"}</p>
      {handoffError ? <p className="v10-validation">{handoffError}</p> : null}
      {previewReady && phaseState?.showPreviewPanel ? (
        <div className="v10-hero-actions">
          <V10Btn onClick={onOpenPreview}>Open preview</V10Btn>
        </div>
      ) : null}
      <V10AgencyExportSuite
        departureKitReady={Boolean(departureKit?.downloadAvailable)}
        exportRunReady={Boolean(exportRunId)}
        onViewEvidence={onScrollToEvidence}
        onOpenApproval={onOpenApprovalSection}
        onDownloadDepartureKit={onDownloadDepartureKit}
        onRequestRunExport={onRequestRunExport}
      />
      <V10ProjectRoomEvidence
        api={api}
        projectId={projectId}
        deployEnv={import.meta.env.VITE_ENREIN_DEPLOY_ENV}
        isExternalProof={isExternalProof}
        exportRunId={exportRunId}
      />
    </section>
  );
}

function LiveCareBlock({
  presentation,
}: {
  readonly presentation?: CanonicalPresentationModelV1 | null;
}) {
  return (
    <section className="v10-panel-block" aria-label="Post-handoff care" data-testid="studio-live-care">
      <h3 data-testid="studio-live-care-title">{presentation?.title ?? "Site Health"}</h3>
      {presentation ? (
        <p className="v10-muted" data-testid="studio-live-care-condition">
          {presentation.condition}
        </p>
      ) : null}
      <p>
        {presentation?.reason ??
          "Post-handoff live care monitors Site Health, safe edits, and outcome proof. Operational workflow starts from care analytics after handoff — this panel is the Studio surface for that continuation."}
      </p>
      {presentation?.primaryAction ? (
        <p className="v10-muted" data-testid="studio-live-care-action">
          Next: {presentation.primaryAction.label}
        </p>
      ) : null}
      <ul>
        <li>Site Health monitor</li>
        <li>Safe edit outcomes</li>
        <li>Outcome proof / change history</li>
      </ul>
    </section>
  );
}

function ControlRoomSectionPanel({
  section,
  blocks,
  projectName,
  modeLabel,
  intakeStatusLabel,
  intakeSummary,
  changeRequestPreview,
  phaseState,
  previewUrl,
  previewOpenGuardInput,
  clientShareGuardInput,
  onWarnShareAcknowledgedChange,
  studioPreviewHtml,
  previewError,
  previewRoutes,
  activePreviewRoute,
  onPreviewRouteChange,
  sitePackageId,
  requestedSitePackageId,
  builtSitePackageId,
  packageEligibilityBlocked,
  packageResolution,
  workflowBusy,
  onBuildWithPackage,
  onEnrichProfile,
  exportRunId,
  handoffError,
  departureKit,
  validationIssues,
  qaIssues,
  qaWarnings,
  importQueueAvailable,
  onReviewTruthProof,
  onReviewImportConfirmation,
  onRetryBuild,
  onCopyClientLink,
  onShareClientLinkFromPreview,
  onCopyPreviewLink,
  onOpenPreview,
  onOpenPortal,
  onDownloadDepartureKit,
  onRequestRunExport,
  onOpenApprovalSection,
  onScrollToEvidence,
  api,
  onImportQueueChanged,
  projectId,
  showRecordCenter,
  liveCarePresentation,
}: {
  readonly section: V10ControlRoomSection;
  readonly blocks: V10PanelBlocks;
  readonly projectName: string;
  readonly modeLabel: string;
  readonly intakeStatusLabel: string;
  readonly intakeSummary: StudioIntakeSummaryV1 | null;
  readonly changeRequestPreview: string | null;
  readonly phaseState: PhaseStateWithFacts | null;
  readonly previewUrl: string | null;
  readonly previewOpenGuardInput: PreviewOpenGuardInput;
  readonly clientShareGuardInput: ClientShareGuardInput;
  readonly onWarnShareAcknowledgedChange: (acknowledged: boolean) => void;
  readonly studioPreviewHtml: string | null;
  readonly previewError: string | null;
  readonly previewRoutes: readonly string[];
  readonly activePreviewRoute: string;
  readonly onPreviewRouteChange: (route: string) => void;
  readonly sitePackageId: string | null;
  readonly requestedSitePackageId: string;
  readonly builtSitePackageId: string | null;
  readonly packageEligibilityBlocked: PackageEligibilityBlocked | null;
  readonly packageResolution: StudioPackageResolutionV1 | null;
  readonly workflowBusy: boolean;
  readonly onBuildWithPackage: (packageId: SitePackageId) => void;
  readonly onEnrichProfile: () => void;
  readonly exportRunId: string | null;
  readonly handoffError: string | null;
  readonly departureKit: StudioDepartureKitStatusV1 | null;
  readonly validationIssues: readonly string[];
  readonly qaIssues: readonly StudioQaIssue[];
  readonly qaWarnings: readonly string[];
  readonly importQueueAvailable: boolean;
  readonly onReviewTruthProof: () => void;
  readonly onReviewImportConfirmation: () => void;
  readonly onRetryBuild: () => void;
  readonly onCopyClientLink: () => void;
  readonly onShareClientLinkFromPreview: () => void;
  readonly onCopyPreviewLink: () => void;
  readonly onOpenPreview: () => void;
  readonly onOpenPortal: () => void;
  readonly onDownloadDepartureKit: () => void;
  readonly onRequestRunExport: () => void;
  readonly onOpenApprovalSection: () => void;
  readonly onScrollToEvidence: () => void;
  readonly api: StudioApiClient;
  readonly onImportQueueChanged?: () => void;
  readonly projectId: string;
  readonly showRecordCenter: boolean;
  readonly liveCarePresentation?: CanonicalPresentationModelV1 | null;
}) {
  const previewReady = isPreviewOpenAllowed(previewOpenGuardInput);

  return (
    <div className="v10-control-panel">
      <h3>{V10_SECTION_LABELS[section]}</h3>
      {blocks.setup ? (
        <>
          <SetupBlock
            intakeStatusLabel={intakeStatusLabel}
            modeLabel={modeLabel}
            onCopyClientLink={onCopyClientLink}
            onOpenPortal={onOpenPortal}
          />
          {showRecordCenter ? <V10RecordCenterPanel api={api} projectId={projectId} /> : null}
        </>
      ) : null}
      {blocks.facts ? (
        <FactsBlock
          phaseState={phaseState}
          intakeSummary={intakeSummary}
          validationIssues={validationIssues}
          qaIssues={qaIssues}
          qaWarnings={qaWarnings}
          requestedSitePackageId={requestedSitePackageId}
          builtSitePackageId={builtSitePackageId}
          previewRoutes={previewRoutes}
          packageEligibilityBlocked={packageEligibilityBlocked}
          packageResolution={packageResolution}
          workflowBusy={workflowBusy}
          importQueueAvailable={importQueueAvailable}
          onBuildWithPackage={onBuildWithPackage}
          onEnrichProfile={onEnrichProfile}
          onReviewTruthProof={onReviewTruthProof}
          onCopyClientIntakeLink={onCopyClientLink}
          onReviewImportConfirmation={onReviewImportConfirmation}
          onRetryBuild={onRetryBuild}
          api={api}
          projectId={projectId}
          onImportQueueChanged={onImportQueueChanged}
          showRecordCenter={showRecordCenter}
        />
      ) : null}
      {blocks.preview ? (
        <PreviewBlock
          projectName={projectName}
          studioPreviewHtml={studioPreviewHtml}
          previewError={previewError}
          previewRoutes={previewRoutes}
          activePreviewRoute={activePreviewRoute}
          onPreviewRouteChange={onPreviewRouteChange}
          previewUrl={previewUrl}
          showPreviewPanel={phaseState?.showPreviewPanel ?? false}
          qaWarnings={qaWarnings}
          previewOpenGuardInput={previewOpenGuardInput}
          clientShareGuardInput={clientShareGuardInput}
          onWarnShareAcknowledgedChange={onWarnShareAcknowledgedChange}
          onShareClientLinkFromPreview={onShareClientLinkFromPreview}
          onOpenPreview={onOpenPreview}
          onCopyPreviewLink={onCopyPreviewLink}
          onResolveQualityIssues={onReviewTruthProof}
        />
      ) : null}
      {blocks.l2Edits ? (
        <L2EditsBlock changeRequestPreview={changeRequestPreview} onOpenPortal={onOpenPortal} />
      ) : null}
      {blocks.approval ? (
        <ApprovalBlock
          phaseState={phaseState}
          previewReady={previewReady}
          clientShareGuardInput={clientShareGuardInput}
          onOpenPreview={onOpenPreview}
          onCopyPreviewLink={onCopyPreviewLink}
          onResolveQualityIssues={onReviewTruthProof}
        />
      ) : null}
      {blocks.handoff ? (
        <HandoffBlock
          phaseState={phaseState}
          departureKit={departureKit}
          sitePackageId={sitePackageId}
          requestedSitePackageId={requestedSitePackageId}
          builtSitePackageId={builtSitePackageId}
          previewRoutes={previewRoutes}
          exportRunId={exportRunId}
          handoffError={handoffError}
          previewReady={previewReady}
          onOpenPreview={onOpenPreview}
          onDownloadDepartureKit={onDownloadDepartureKit}
          onRequestRunExport={onRequestRunExport}
          onOpenApprovalSection={onOpenApprovalSection}
          onScrollToEvidence={onScrollToEvidence}
          api={api}
          projectId={projectId}
          isExternalProof={showRecordCenter}
        />
      ) : null}
      {blocks.liveCare ? <LiveCareBlock presentation={liveCarePresentation} /> : null}
    </div>
  );
}

export function V10ProjectRoomView({
  route,
  onNavigate,
  onSectionNavigate,
  phaseState,
  sectionProgress,
  projectLoading,
  projectLoadError,
  businessName,
  intakeStatusLabel,
  intakeSummary,
  changeRequestPreview,
  previewUrl,
  previewOpenGuardInput,
  clientShareGuardInput,
  onWarnShareAcknowledgedChange,
  studioPreviewHtml,
  previewError,
  previewRoutes,
  activePreviewRoute,
  onPreviewRouteChange,
  sitePackageId,
  requestedSitePackageId,
  builtSitePackageId,
  packageEligibilityBlocked,
  packageResolution,
  onBuildWithPackage,
  onEnrichProfile,
  exportRunId,
  handoffError,
  departureKit,
  workflowBusy,
  validationIssues,
  qaIssues,
  qaWarnings,
  importQueueAvailable,
  onPrimaryAction,
  onCopyClientLink,
  onShareClientLinkFromPreview,
  onCopyPreviewLink,
  onOpenPreview,
  onOpenPortal,
  api,
  onImportQueueChanged,
  liveCarePresentation = null,
}: {
  readonly route: V10RouteState;
  readonly onNavigate: V10Navigate;
  readonly onSectionNavigate: V10Navigate;
  readonly phaseState: PhaseStateWithFacts | null;
  readonly sectionProgress: Record<V10ControlRoomSection, V10SectionProgressStatus> | null;
  readonly projectLoading: boolean;
  readonly projectLoadError: string | null;
  readonly businessName: string;
  readonly intakeStatusLabel: string;
  readonly intakeSummary: StudioIntakeSummaryV1 | null;
  readonly changeRequestPreview: string | null;
  readonly previewUrl: string | null;
  readonly previewOpenGuardInput: PreviewOpenGuardInput;
  readonly clientShareGuardInput: ClientShareGuardInput;
  readonly onWarnShareAcknowledgedChange: (acknowledged: boolean) => void;
  readonly studioPreviewHtml: string | null;
  readonly previewError: string | null;
  readonly previewRoutes: readonly string[];
  readonly activePreviewRoute: string;
  readonly onPreviewRouteChange: (route: string) => void;
  readonly sitePackageId: string | null;
  readonly requestedSitePackageId: string;
  readonly builtSitePackageId: string | null;
  readonly packageEligibilityBlocked: PackageEligibilityBlocked | null;
  readonly packageResolution: StudioPackageResolutionV1 | null;
  readonly onBuildWithPackage: (packageId: SitePackageId) => void;
  readonly onEnrichProfile: () => void;
  readonly exportRunId: string | null;
  readonly handoffError: string | null;
  readonly departureKit: StudioDepartureKitStatusV1 | null;
  readonly workflowBusy: boolean;
  readonly validationIssues: readonly string[];
  readonly qaIssues: readonly StudioQaIssue[];
  readonly qaWarnings: readonly string[];
  readonly importQueueAvailable: boolean;
  readonly onPrimaryAction: (actionId: string) => void;
  readonly onCopyClientLink: () => void;
  readonly onShareClientLinkFromPreview: () => void;
  readonly onCopyPreviewLink: () => void;
  readonly onOpenPreview: () => void;
  readonly onOpenPortal: () => void;
  readonly api: StudioApiClient;
  readonly onImportQueueChanged?: () => void;
  readonly liveCarePresentation?: CanonicalPresentationModelV1 | null;
}) {
  const projectId = route.projectId;
  if (!projectId) {
    return <p className="v10-muted">Select a project from Projects or Today.</p>;
  }
  const modeLabel = route.mode && isV10ProjectMode(route.mode) ? V10_MODE_LABELS[route.mode] : V10_MODE_LABELS.DEFAULT_GENERATION;
  const section = route.section ?? "overview";
  const showRecordCenter = route.mode === "EXTERNAL_PROOF_ONLY";
  const panelBlocks = resolveV10PanelBlocks({ section, phaseState, changeRequestPreview });
  const overrideHeroForWarn = shouldOverrideHeroForQaWarn(clientShareGuardInput);

  const goSection = (next: V10ControlRoomSection) => {
    onSectionNavigate(v10ProjectRoute({ projectId, section: next, mode: route.mode ?? undefined }));
  };

  const heroCta = phaseState
    ? {
        label: overrideHeroForWarn ? CLIENT_SHARE_QA_WARN_PRIMARY_CTA : phaseState.primaryCta,
        disabled: phaseState.primaryDisabled || workflowBusy,
      }
    : { label: "Loading…", disabled: true };
  // When a canonical Agency model owns the hero and carries no primary action, Studio must not
  // present one — neither as a button nor as a next-action label.
  const heroPrimaryAbsent =
    !overrideHeroForWarn &&
    phaseState?.heroPresentation != null &&
    phaseState.heroPresentation.primaryAction == null;

  const onHeroPrimary = () => {
    if (!phaseState || phaseState.primaryActionId === "none") return;
    if (overrideHeroForWarn) {
      onPrimaryAction("resolve-quality-issues");
      return;
    }
    onPrimaryAction(phaseState.primaryActionId);
  };

  const onReviewTruthProof = () => {
    goSection("business");
  };

  const onReviewImportConfirmation = () => {
    goSection("business");
    if (typeof document !== "undefined") {
      document.getElementById("v10-import-confirmation")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const onScrollToEvidence = () => {
    goSection("business");
    if (typeof document !== "undefined") {
      document.querySelector(".v10-project-evidence-stack")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const onOpenApprovalSection = () => goSection("approval-delivery");

  const onDownloadDepartureKit = () => onPrimaryAction("download-handoff");

  const onRequestRunExport = () => onPrimaryAction("package-handoff");

  const onRetryBuild = () => {
    onPrimaryAction("confirm-build");
  };

  if (projectLoading && !phaseState) {
    return <p className="v10-muted">Loading project…</p>;
  }
  if (projectLoadError) {
    return <p className="v10-validation">{projectLoadError}</p>;
  }

  return (
    <section className="v10-screen" aria-label="Project Control Room">
      <div className="v10-identity-strip">
        <V10Btn variant="line" onClick={() => onNavigate(v10ViewRoute("today"))}>
          ← Today
        </V10Btn>
        <V10Btn variant="line" onClick={() => onNavigate(v10ViewRoute("projects"))}>
          All projects
        </V10Btn>
        <strong>{businessName}</strong>
        <span className="v10-pill">{modeLabel}</span>
      </div>

      <V10PhaseStrip
        activeSection={section}
        sectionProgress={sectionProgress}
        onSectionChange={goSection}
      />

      <V10DomainAtlasNav section={section} onNavigateSection={goSection} />

      <div className="v10-hero v10-hero--paper" data-testid="studio-project-hero">
        <div>
          <V10Kicker>Project Control Room / {V10_SECTION_LABELS[section]}</V10Kicker>
          <h1 className="v10-display-title v10-display-title--paper" data-testid="studio-hero-title">
            {overrideHeroForWarn
              ? "Preview generated with warnings"
              : (phaseState?.nowTitle ?? `${businessName} — ${V10_SECTION_LABELS[section]}`)}
          </h1>
          {phaseState?.heroPresentation && !overrideHeroForWarn ? (
            <p className="v10-muted" data-testid="studio-hero-condition">
              {phaseState.heroPresentation.condition}
            </p>
          ) : null}
          <p className="v10-lede v10-lede--paper">
            {overrideHeroForWarn
              ? "Not client-ready until quality issues are resolved. Review Truth / Proof before sharing."
              : (phaseState?.nowCopy ?? "One dominant next action.")}
          </p>
          {phaseState?.showStaleApprovalBanner ? (
            <p className="v10-validation">{phaseState.staleApprovalSummary}</p>
          ) : null}
        </div>
        <aside className="v10-hero-card v10-hero-card--paper">
          <V10Kicker>Next action</V10Kicker>
          <div className="v10-metric v10-metric--paper" data-testid="studio-hero-next-action">
            {heroPrimaryAbsent ? "No action needed right now" : heroCta.label}
          </div>
          {heroPrimaryAbsent && phaseState?.heroPresentation ? (
            // Canonical model has no primary action — do not offer an invented one.
            <p className="v10-muted" data-testid="studio-hero-no-action">
              {phaseState.heroPresentation.waitingOn
                ? `Waiting on ${phaseState.heroPresentation.waitingOn}`
                : "Nothing is blocked."}
            </p>
          ) : (
            <V10Btn
              variant="gold"
              disabled={heroCta.disabled}
              onClick={onHeroPrimary}
              testId="studio-hero-cta"
            >
              {heroCta.label}
            </V10Btn>
          )}
        </aside>
      </div>

      <ControlRoomSectionPanel
        section={section}
        blocks={panelBlocks}
        projectName={businessName}
        modeLabel={modeLabel}
        intakeStatusLabel={intakeStatusLabel}
        intakeSummary={intakeSummary}
        changeRequestPreview={changeRequestPreview}
        phaseState={phaseState}
        previewUrl={previewUrl}
        previewOpenGuardInput={previewOpenGuardInput}
        clientShareGuardInput={clientShareGuardInput}
        onWarnShareAcknowledgedChange={onWarnShareAcknowledgedChange}
        studioPreviewHtml={studioPreviewHtml}
        previewError={previewError}
        previewRoutes={previewRoutes}
        activePreviewRoute={activePreviewRoute}
        onPreviewRouteChange={onPreviewRouteChange}
        sitePackageId={sitePackageId}
        requestedSitePackageId={requestedSitePackageId}
        builtSitePackageId={builtSitePackageId}
        packageEligibilityBlocked={packageEligibilityBlocked}
        packageResolution={packageResolution}
        workflowBusy={workflowBusy}
        onBuildWithPackage={onBuildWithPackage}
        onEnrichProfile={onEnrichProfile}
        exportRunId={exportRunId}
        handoffError={handoffError}
        departureKit={departureKit}
        validationIssues={validationIssues}
        qaIssues={qaIssues}
        qaWarnings={qaWarnings}
        importQueueAvailable={importQueueAvailable}
        onReviewTruthProof={onReviewTruthProof}
        onReviewImportConfirmation={onReviewImportConfirmation}
        onRetryBuild={onRetryBuild}
        onCopyClientLink={onCopyClientLink}
        onShareClientLinkFromPreview={onShareClientLinkFromPreview}
        onCopyPreviewLink={onCopyPreviewLink}
        onOpenPreview={onOpenPreview}
        onOpenPortal={onOpenPortal}
        onDownloadDepartureKit={onDownloadDepartureKit}
        onRequestRunExport={onRequestRunExport}
        onOpenApprovalSection={onOpenApprovalSection}
        onScrollToEvidence={onScrollToEvidence}
        api={api}
        onImportQueueChanged={onImportQueueChanged}
        projectId={projectId}
        showRecordCenter={showRecordCenter}
        liveCarePresentation={liveCarePresentation}
      />
    </section>
  );
}
